import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { chatApi } from '@/api'
import {
  generateFaviconDataUrl,
  parsePlatformFaviconCustomization,
  PLATFORM_FAVICON_SETTING_KEY,
} from '@/helpers/faviconCustomization'
import { selectIsAuth } from '@/store/profile.reducer'
import websocket from '@/utils/websocket'

const CHAT_NOTIFICATION_TYPES = new Set(['chat_new_message', 'chat.read'])

const getNotification = (payload) => {
  const notification = payload?.notification

  if (typeof notification !== 'string') return notification

  try {
    return JSON.parse(notification)
  } catch {
    return null
  }
}

const isChatNotification = (payload) =>
  CHAT_NOTIFICATION_TYPES.has(getNotification(payload)?.type)

const getUnreadCount = (response) => {
  const items = response?.data?.noReadMessages
  if (!Array.isArray(items)) return 0

  return items.reduce((total, item) => {
    const count = Number(item?.count)
    return total + (Number.isFinite(count) ? Math.max(0, count) : 0)
  }, 0)
}

const restoreFallbackFavicon = (link) => {
  link.dataset.customFaviconActive = 'false'
  link.removeAttribute('type')

  const fallback =
    link.dataset.platformFaviconFallback ||
    link.dataset.platformFaviconDefault

  if (fallback) link.href = fallback
}

const PlatformFavicon = () => {
  const isAuth = useSelector(selectIsAuth)
  const storedSetting = useSelector(
    (store) => store.settings.custom?.[PLATFORM_FAVICON_SETTING_KEY],
  )
  const settings = useMemo(
    () => parsePlatformFaviconCustomization(storedSetting),
    [storedSetting],
  )
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    if (!isAuth || !settings.svg || !settings.showUnreadCount) {
      setUnreadCount(0)
      return undefined
    }

    let isCurrent = true
    let refreshTimer = null
    let requestId = 0

    const refreshUnreadCount = () => {
      const currentRequestId = ++requestId

      chatApi
        .getActiveUsers()
        .then((response) => {
          if (isCurrent && currentRequestId === requestId) {
            setUnreadCount(getUnreadCount(response))
          }
        })
        .catch(() => {})
    }

    const scheduleRefresh = () => {
      requestId += 1
      window.clearTimeout(refreshTimer)
      refreshTimer = window.setTimeout(refreshUnreadCount, 150)
    }

    const handleNotification = (payload) => {
      if (isChatNotification(payload)) scheduleRefresh()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') scheduleRefresh()
    }

    refreshUnreadCount()
    websocket.connect()

    const unsubscribeOpen = websocket.subscribeOpen(scheduleRefresh)
    const unsubscribeChatRead = websocket.subscribe('chat.read', scheduleRefresh)
    const unsubscribeNotification = websocket.subscribe(
      'notification',
      handleNotification,
    )
    const unsubscribeNotifications = websocket.subscribe(
      'notifications',
      handleNotification,
    )

    window.addEventListener('focus', scheduleRefresh)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      isCurrent = false
      requestId += 1
      window.clearTimeout(refreshTimer)
      unsubscribeOpen?.()
      unsubscribeChatRead?.()
      unsubscribeNotification?.()
      unsubscribeNotifications?.()
      window.removeEventListener('focus', scheduleRefresh)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuth, settings.showUnreadCount, settings.svg])

  useEffect(() => {
    const link = document.getElementById('dynamic-favicon')
    if (!link) return undefined

    if (!link.dataset.platformFaviconDefault) {
      link.dataset.platformFaviconDefault = link.href
    }

    if (!settings.svg) {
      restoreFallbackFavicon(link)
      return undefined
    }

    let isCurrent = true
    link.dataset.customFaviconActive = 'true'

    generateFaviconDataUrl(settings, unreadCount)
      .then((faviconUrl) => {
        if (!isCurrent) return
        if (!faviconUrl) {
          restoreFallbackFavicon(link)
          return
        }

        link.dataset.customFaviconActive = 'true'
        link.type = 'image/png'
        link.href = faviconUrl
      })
      .catch(() => {
        if (isCurrent) restoreFallbackFavicon(link)
      })

    return () => {
      isCurrent = false
    }
  }, [settings, unreadCount])

  return null
}

export default PlatformFavicon
